import { memo, type ReactNode } from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import { EditCalendar, EventAvailable, Person, Schedule, Timer } from "@mui/icons-material";
import dayjs from "dayjs";
import { FREE_SLOTS_DAYS_AHEAD } from "../services/agendaService";
import type { TeleconsultoriaAgendaSummary } from "../types";

interface TeleconsultoriaScheduleCardProps {
  agenda: TeleconsultoriaAgendaSummary;
  freeSlots?: number;
  /** Visão clínica: mostra o profissional dono da agenda. */
  showPractitioner?: boolean;
  onOpen: (agenda: TeleconsultoriaAgendaSummary) => void;
  onEdit: (agenda: TeleconsultoriaAgendaSummary) => void;
}

function Info({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
      <Box sx={{ display: "flex", color: "text.secondary" }} aria-hidden>
        {icon}
      </Box>
      <Typography variant="body2" color="text.secondary" noWrap>
        {text}
      </Typography>
    </Stack>
  );
}

function periodText(agenda: TeleconsultoriaAgendaSummary): string {
  const start = agenda.periodStart ? dayjs(agenda.periodStart).format("DD/MM/YYYY") : "—";
  const end = agenda.periodEnd ? dayjs(agenda.periodEnd).format("DD/MM/YYYY") : "—";
  return `${start} a ${end}`;
}

/** Card da agenda: o essencial à vista e as ações a um toque. */
export const TeleconsultoriaScheduleCard = memo(function TeleconsultoriaScheduleCard({
  agenda,
  freeSlots,
  showPractitioner = false,
  onOpen,
  onEdit,
}: TeleconsultoriaScheduleCardProps) {
  const details = [
    agenda.slotDurationMinutes ? `${agenda.slotDurationMinutes} min por consulta` : "",
    agenda.anticipationDays ? `libera ${agenda.anticipationDays} dias antes` : "",
  ].filter(Boolean);

  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        borderLeft: "4px solid",
        borderLeftColor: agenda.isActive ? "primary.main" : "divider",
        borderRadius: "0 8px 8px 0",
        opacity: agenda.isActive ? 1 : 0.75,
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
            {agenda.name}
          </Typography>
          <Chip
            size="small"
            label={agenda.isActive ? "Ativa" : "Inativa"}
            color={agenda.isActive ? "success" : "default"}
            variant={agenda.isActive ? "filled" : "outlined"}
          />
        </Stack>
        {showPractitioner && agenda.practitioner?.display && (
          <Info icon={<Person fontSize="small" />} text={agenda.practitioner.display} />
        )}
        <Info icon={<Schedule fontSize="small" />} text={periodText(agenda)} />
        {details.length > 0 && <Info icon={<Timer fontSize="small" />} text={details.join(" · ")} />}
        {agenda.healthcareServiceName && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {agenda.healthcareServiceName}
            {agenda.locationName ? ` · ${agenda.locationName}` : ""}
          </Typography>
        )}
        {agenda.isActive && typeof freeSlots === "number" && (
          <Chip
            size="small"
            variant="outlined"
            color={freeSlots ? "primary" : "warning"}
            icon={<EventAvailable />}
            label={`${freeSlots} horário${freeSlots === 1 ? "" : "s"} livre${freeSlots === 1 ? "" : "s"} em ${FREE_SLOTS_DAYS_AHEAD} dias`}
            sx={{ alignSelf: "flex-start" }}
          />
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button size="small" variant="contained" onClick={() => onOpen(agenda)} sx={{ textTransform: "none" }}>
            Ver horários e consultas
          </Button>
          <Button size="small" startIcon={<EditCalendar />} onClick={() => onEdit(agenda)} sx={{ textTransform: "none" }}>
            Editar
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
});
